import React, {
    FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrderKamonComponent } from '@component/index';
import {
    submitPartSelect,
    hideEcHeaderNavi,
} from '@root/app/react/common/Function';
import { useHistoryGoParts } from '@app/react/contexts/useHistoryGoParts';
import {
    apiGetActions,
    partsSelectActions,
} from '@app/react/action';
import { setFamilycrestOk } from '@app/react/action/PartsSelectAction';
import { showOrderPartsHelpAction } from '@app/react/action/showOrderPartsHelp';
import {
    GetResponseOrderParts,
    GetResponseFamilyCrestColors,
    PartsProduct,
} from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import { OnSelectFamilyCrestColor } from '@app/react/uikit';
import { IinitState } from '@app/react/reducer';

import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import {
    noProduct,
    yesProduct as originYesProduct,
} from '@app/react/const/products/familycrest';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { selectOrderPartsHelp } from './selectors';

type OnSelect = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    product: PartsProduct
) => void;

const selectFamilycrestOk = (state: AppState) => state.customize.familycrestOk;

export const OrderKamonContainer: FC<{ waitFetch?: boolean }> = ({ waitFetch = false }) => {
    const [show, setShow] = useState<boolean>(false);
    const dispatch = useDispatch();
    const { arrowType, orderType } = useOrderPathParams();

    const familycrestData = useSelector<AppState, GetResponseOrderParts | null>(
        (state) => state.APIData.familycrest,
    );
    const familycrestColorsData = useSelector<
    AppState,
    GetResponseFamilyCrestColors | null
    >((state) => state.APIData.familycrestColor);

    const yesProduct = useMemo(() => {
        const prices = familycrestData?.partsData.map((item) => item.price);
        if (prices === undefined) {
            return originYesProduct;
        }
        const min = Math.min(...prices);
        return {
            ...originYesProduct,
            price: min,
        };
    }, [familycrestData]);

    const customizeData = useSelector<AppState, IinitState>(
        (state) => state.customize,
    );
    const familycrestOk = useSelector(selectFamilycrestOk);

    const openHelpParts = useSelector(selectOrderPartsHelp);
    const isOpenHelp = openHelpParts === 'familycrest';

    const goParts = useHistoryGoParts();

    const handlerCustomClick = useCallback(() => {
        goParts('preview');
    }, [goParts]);

    const onClickNoProduct = () => dispatch(setFamilycrestOk({ ok: false }));
    const onClickYesProduct = () => dispatch(setFamilycrestOk({ ok: true }));

    const onSelectProduct: OnSelect = (event, product) => {
        submitPartSelect(dispatch, partsSelectActions.familycrest.started, {
            param: product,
        });
    };

    const onSelectFamilyCrestColor: OnSelectFamilyCrestColor = (event, product) => {
        dispatch(
            partsSelectActions.fafamilycrestColor.started({ param: product }),
        );
    };

    const openHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: 'familycrest' }));
    }, [dispatch]);

    const closeHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: null }));
    }, [dispatch]);

    const isDesktop: boolean = useMediaDesktop();
    const isMobile: boolean = useMediaMobile();

    useEffect(() => {
        hideEcHeaderNavi(isDesktop, isMobile, dispatch);
    }, [isDesktop, isMobile, dispatch]);

    useEffect(() => {
        if (waitFetch) {
            console.log('initializing, waiting fetch');
            return;
        }
        dispatch(
            apiGetActions.familycrest.started({
                arrowType,
                orderType,
                selectItems: customizeData,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, arrowType, orderType, waitFetch]);

    return (
        <OrderKamonComponent
            noProduct={noProduct}
            yesProduct={yesProduct}
            onCustomizeClick={handlerCustomClick}
            familycrestData={familycrestData}
            familycrestColorsData={familycrestColorsData}
            familycrestOk={familycrestOk}
            familycrest={customizeData.familycrest}
            familycrestColor={customizeData.familycrestColor}
            onClickNoProduct={onClickNoProduct}
            onClickYesProduct={onClickYesProduct}
            onSelectProduct={onSelectProduct}
            onSelectFamilyCrestColor={onSelectFamilyCrestColor}
            isPanel={show}
            showPanel={() => setShow(true)}
            hidePanel={() => setShow(false)}
            isHelpOpen={isOpenHelp}
            handleHelpOpen={openHelp}
            handleHelpClose={closeHelp}
        />
    );
};
